import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useLanguage } from '../StoreContext/LanguageContext';
import DownloadModal from '../Components/Download/DownloadModal';
import { Download, Plus, Edit, Trash2, ChevronLeft, ChevronRight, Search, MapPin, Users, Building, Activity } from "lucide-react";
import apiService from '../config';

const defaultForm = {
  villageName: '',
  talukaName: '',
  stateCode: 'Maharashtra',
  pinCode: '',
  censusCode: '',
  latitude: '',
  longitude: '',
};

const VillageManagement = () => {
  const [villages, setVillages] = useState([]);
  const [filteredVillages, setFilteredVillages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false); // NEW: Form loading state
  const [toggleLoading, setToggleLoading] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const { language, translations } = useLanguage();

  const { register, handleSubmit, reset, setValue } = useForm({
    defaultValues: defaultForm,
  });

  // Fetch villages from API using apiService
  useEffect(() => {
    const fetchVillages = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const result = await apiService.getAllVillages();
        
        if (result.status === 200 && result.data) {
          const transformedData = result.data.map(village => ({
            villageId: village.VILL_ID || village.village_id,
            villageName: village.village_name,
            engVillageName: village.village_name,
            talukaName: village.taluka_name,
            engTalukaName: village.taluka_name,
            districtName: village.dist_name,
            engDistrictName: village.dist_name,
            stateCode: 'Maharashtra',
            pinCode: village.pincode,
            censusCode: village.censuscode,
            tehsilNo: village.taluka_no,
            distNo: village.dist_no,
            fvtm: village.fvtm,
            geographicalArea: village.GeographicalArea_KM,
            population: village.population,
            status: village.is_active,
            createdDate: new Date().toISOString(),
            lastUpdatedDate: new Date().toISOString(),
          }));
          setVillages(transformedData);
          setFilteredVillages(transformedData);
        } else {
          throw new Error(result.message || 'Failed to fetch villages data');
        }
      } catch (err) {
        console.error('Error fetching villages:', err);
        
        if (err.message.includes('Network Error')) {
          setError('Network error: Unable to connect to the server. Please check your internet connection.');
        } else if (err.message.includes('404')) {
          setError('API endpoint not found. Please verify the API configuration.');
        } else if (err.message.includes('500')) {
          setError('Server error: The API is experiencing issues. Please try again later.');
        } else if (err.message.includes('timeout')) {
          setError('Request timeout: The server is taking too long to respond. Please try again.');
        } else {
          setError(`Error: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchVillages();
  }, []);

  // Search functionality
  useEffect(() => {
    const filtered = villages.filter(village =>
      village.villageName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      village.talukaName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      village.districtName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      village.pinCode?.toString().includes(searchTerm) ||
      village.censusCode?.toString().includes(searchTerm)
    );
    setFilteredVillages(filtered);
    setCurrentPage(1);
  }, [searchTerm, villages]);

  // Pagination logic
  const totalPages = Math.ceil(filteredVillages.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentVillages = filteredVillages.slice(startIndex, endIndex);

  // Statistics
  const totalVillages = villages.length;
  const activeVillages = villages.filter(v => v.status).length;
  const totalPopulation = villages.reduce((sum, v) => sum + (parseInt(v.population) || 0), 0);
  const totalArea = villages.reduce((sum, v) => sum + (parseFloat(v.geographicalArea) || 0), 0);

  // Define columns for download
  const downloadColumns = [
    { header: 'S.No.', value: 'index' },
    { header: translations[language]?.VillageName || 'Village Name', value: 'villageName' },
    { header: 'English Village Name', value: 'engVillageName' },
    { header: translations[language]?.TalukaName || 'Taluka Name', value: 'talukaName' },
    { header: 'English Taluka Name', value: 'engTalukaName' },
    { header: 'District Name', value: 'districtName' },
    { header: 'English District Name', value: 'engDistrictName' },
    { header: translations[language]?.StateCode || 'State Code', value: 'stateCode' },
    { header: translations[language]?.Pin || 'PIN Code', value: 'pinCode' },
    { header: translations[language]?.CensusCode || 'Census Code', value: 'censusCode' },
    { header: 'Geographical Area (KM)', value: 'geographicalArea' },
    { header: 'Population', value: 'population' },
    // { header: translations[language]?.Status || 'Status', value: 'status' },
  ];

  const retryFetch = () => {
    setError(null);
    const fetchVillages = async () => {
      try {
        setLoading(true);
        const result = await apiService.getAllVillages();
        
        if (result.status === 200 && result.data) {
          const transformedData = result.data.map(village => ({
            villageId: village.VILL_ID || village.village_id,
            villageName: village.village_name,
            engVillageName: village.eng_village_name,
            talukaName: village.taulka_name,
            engTalukaName: village.taluka_name,
            districtName: village.dist_name,
            engDistrictName: village.dist_name,
            stateCode: 'Maharashtra',
            pinCode: village.pincode,
            censusCode: village.censuscode,
            tehsilNo: village.taluka_no,
            distNo: village.dist_no,
            fvtm: village.fvtm,
            geographicalArea: village.GeographicalArea_KM,
            population: village.population,
            status: true,
            createdDate: new Date().toISOString(),
            lastUpdatedDate: new Date().toISOString(),
          }));
          setVillages(transformedData);
          setFilteredVillages(transformedData);
        } else {
          throw new Error(result.message || 'Failed to fetch villages data');
        }
      } catch (err) {
        console.error('Error fetching villages:', err);
        setError(`Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchVillages();
  };

  const handleToggle = async (villageId) => {
    try {
      setToggleLoading(prev => ({ ...prev, [villageId]: true }));

      const result = await apiService.updateVillageActivity(villageId);

      if (result.status === 200) {
        setVillages(prevVillages =>
          prevVillages.map(village =>
            village.villageId === villageId
              ? { ...village, status: !village.status, lastUpdatedDate: new Date().toISOString() }
              : village
          )
        );
        console.log('Village status updated successfully:', result.message);
      } else {
        throw new Error(result.message || 'Failed to update village status');
      }
    } catch (err) {
      console.error('Error updating village status:', err);
      alert(`Failed to update village status: ${err.message}`);
    } finally {
      setToggleLoading(prev => {
        const newState = { ...prev };
        delete newState[villageId];
        return newState;
      });
    }
  };

  // UPDATED: onSubmit function with loading state
  const onSubmit = async (data) => {
    setIsFormLoading(true); // Start loading
    
    try {
      const formData = { ...data, stateCode: 'Maharashtra' };
      
      if (editIndex !== null) {
        // Edit existing village
        const updated = [...villages];
        updated[editIndex] = { ...updated[editIndex], ...formData, lastUpdatedDate: new Date().toISOString() };
        setVillages(updated);
        setEditIndex(null);
        
        // Simulate API call delay for demonstration
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        alert('✅ Village updated successfully!');
      } else {
        // Add new village
        const newVillage = {
          ...formData,
          villageId: Date.now(),
          districtName: 'Buldhana',
          stateName: 'Maharashtra',
          status: true,
          createdDate: new Date().toISOString(),
          lastUpdatedDate: new Date().toISOString(),
        };
        
        // Simulate API call delay for demonstration
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        setVillages([...villages, newVillage]);
        alert('✅ Village added successfully!');
      }
      
      reset(defaultForm);
      setShowFormModal(false);
    } catch (error) {
      console.error('Error saving village:', error);
      alert('❌ Failed to save village. Please try again.');
    } finally {
      setIsFormLoading(false); // Stop loading
    }
  };

  const handleEdit = (index) => {
    const village = villages[index];
    Object.keys(defaultForm).forEach((key) => {
      setValue(key, village[key]);
    });
    setEditIndex(index);
    setShowFormModal(true);
  };

  const handleAddNew = () => {
    reset(defaultForm);
    setEditIndex(null);
    setShowFormModal(true);
  };

  const handleDelete = (index) => {
    if (window.confirm('Are you sure you want to delete this village?')) {
      const updated = villages.filter((_, i) => i !== index);
      setVillages(updated);
    }
  };

  const closeFormModal = () => {
    if (isFormLoading) return; // Prevent closing during loading
    setShowFormModal(false);
    setEditIndex(null);
    reset(defaultForm);
  };

  const handleDownload = () => {
    if (villages.length === 0) {
      alert('No data available to download');
      return;
    }
    setShowDownloadModal(true);
  };

  const getDownloadData = () => {
    return villages.map((village, index) => ({
      ...village,
      index: index + 1,
      status: village.status 
        ? (translations[language]?.Active || 'Active')
        : (translations[language]?.Inactive || 'Inactive')
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6 max-w-7xl mx-auto">


        {/* Search and Filter Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={translations[language]?.searchVillages}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700">{translations[language]?.show}:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="text-sm text-gray-600">
                {translations[language]?.showing} {startIndex + 1}-{Math.min(endIndex, filteredVillages.length)} {translations[language]?.of} {filteredVillages.length}
              </span>
            </div>
                        <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleDownload}
                className="flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-all duration-200"
                disabled={villages.length === 0}
              >
                <Download className="mr-2 h-4 w-4" /> 
                {translations[language]?.export || 'Export'}
              </button>
              {/* <button 
                onClick={handleAddNew}
                className="flex items-center justify-center px-6 py-2.5 bg-gradient-to-r from-blue-600 to-gray-900 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Plus className="mr-2 h-4 w-4" />
                {translations[language]?.AddVillage || 'Add Village'}
              </button> */}
            </div>
          </div>
        </div>

        {/* Enhanced Loading State */}
        {loading && (
          <div className="flex justify-center items-center h-80">
            <div className="text-center animate-float">
              <div className="inline-flex relative">
                <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
                <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-purple-400 animate-pulse" />
                </div>
              </div>
              <p className="text-gray-400 mt-4">{translations[language]?.loadingVillages}</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-white rounded-xl shadow-sm border border-red-200 p-6 mb-6">
            <div className="bg-red-50 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <div className="text-red-600">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-2">
                    <span className="text-red-800 font-medium">{error}</span>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{translations[language]?.commonCauses}:</p>
                      <ul className="list-disc list-inside ml-2 mt-1">
                        <li>{translations[language]?.apiServiceIssues}</li>
                        <li>{translations[language]?.serverNotResponding}</li>
                        <li>{translations[language]?.authTokenExpired}</li>
                        <li>{translations[language]?.networkIssues}</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <button
                  onClick={retryFetch}
                  className="ml-4 px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors duration-200"
                >
                  {translations[language]?.retry}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        {!loading && !error && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{translations[language]?.sNo}</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{translations[language]?.VillageName || 'Village Name'}</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{translations[language]?.TalukaName || 'Taluka Name'}</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{translations[language]?.district}</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{translations[language]?.StateCode || 'State Code'}</th>
                    {/* <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{translations[language]?.Pin || 'PIN'}</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{translations[language]?.CensusCode || 'Census Code'}</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Area (KM²)</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Population</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{translations[language]?.Status || 'Status'}</th> */}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentVillages.map((village, index) => (
                    <tr key={village.villageId} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {startIndex + index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{village.villageName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{village.talukaName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{village.districtName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{village.stateCode}</td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{village.pinCode}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{village.censusCode}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{village.geographicalArea}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{village.population}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            onClick={() => !toggleLoading[village.villageId] && handleToggle(village.villageId)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                              toggleLoading[village.villageId] 
                                ? "bg-gray-400 cursor-not-allowed" 
                                : village.status 
                                  ? "bg-green-600 cursor-pointer" 
                                  : "bg-gray-300 cursor-pointer"
                            }`}
                          >
                            {toggleLoading[village.villageId] ? (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                              </div>
                            ) : (
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 shadow-sm ${
                                  village.status ? "translate-x-6" : "translate-x-1"
                                }`}
                              />
                            )}
                          </div>
                          <span className={`ml-3 text-sm font-medium ${
                            village.status ? "text-green-600" : "text-gray-500"
                          }`}>
                            {village.status ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </td> */}
                    </tr>
                  ))}
                  {currentVillages.length === 0 && !loading && (
                    <tr>
                      <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                        <div className="flex flex-col items-center">
                          <Building className="h-12 w-12 text-gray-300 mb-4" />
                          <p className="text-lg font-medium">
                            {searchTerm ? 'No villages found matching your search' : (translations[language]?.NoVillages || 'No villages found.')}
                          </p>
                          {searchTerm && (
                            <button
                              onClick={() => setSearchTerm('')}
                              className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                            >
                              {translations[language]?.clearSearchResults}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    {translations[language]?.showing} {startIndex + 1} {translations[language]?.to} {Math.min(endIndex, filteredVillages.length)} {translations[language]?.of} {filteredVillages.length} {translations[language]?.results}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    
                    <div className="flex space-x-1">
                      {[...Array(Math.min(5, totalPages))].map((_, index) => {
                        let pageNumber;
                        if (totalPages <= 5) {
                          pageNumber = index + 1;
                        } else if (currentPage <= 3) {
                          pageNumber = index + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNumber = totalPages - 4 + index;
                        } else {
                          pageNumber = currentPage - 2 + index;
                        }
                        
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => setCurrentPage(pageNumber)}
                            className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border rounded-lg transition-colors duration-200 ${
                              currentPage === pageNumber
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Form Modal */}
        {showFormModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-slideUp">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {editIndex !== null ? 'Edit Village' : (translations[language]?.AddVillage || 'Add Village')}
                  </h3>
                  <button 
                    onClick={closeFormModal}
                    className={`text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2 hover:bg-gray-100 rounded-lg ${
                      isFormLoading ? 'cursor-not-allowed opacity-50' : ''
                    }`}
                    disabled={isFormLoading}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <form onSubmit={handleSubmit(onSubmit)} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {translations[language]?.VillageName || 'Village Name'} *
                    </label>
                    <input 
                      {...register('villageName')} 
                      placeholder={translations[language]?.VillageName || 'Village Name'} 
                      className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed" 
                      required 
                      disabled={isFormLoading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {translations[language]?.TalukaName || 'Taluka Name'} *
                    </label>
                    <input 
                      {...register('talukaName')} 
                      placeholder={translations[language]?.TalukaName || 'Taluka Name'} 
                      className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed" 
                      required 
                      disabled={isFormLoading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {translations[language]?.StateCode || 'State Code'}
                    </label>
                    <input 
                      {...register('stateCode')} 
                      className="w-full border border-gray-300 px-4 py-3 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed" 
                      value="Maharashtra"
                      readOnly 
                      disabled={isFormLoading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {translations[language]?.PinCode || 'PIN Code'} *
                    </label>
                    <input 
                      {...register('pinCode')} 
                      placeholder={translations[language]?.PinCode || 'PIN Code'} 
                      className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed" 
                      required 
                      disabled={isFormLoading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {translations[language]?.CensusCode || 'Census Code'}
                    </label>
                    <input 
                      {...register('censusCode')} 
                      placeholder={translations[language]?.CensusCode || 'Census Code'} 
                      className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed" 
                      disabled={isFormLoading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {translations[language]?.Latitude || 'Latitude'}
                    </label>
                    <input 
                      {...register('latitude')} 
                      placeholder={translations[language]?.Latitude || 'Latitude'} 
                      className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed" 
                      type="number"
                      step="any"
                      disabled={isFormLoading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {translations[language]?.Longitude || 'Longitude'}
                    </label>
                    <input 
                      {...register('longitude')} 
                      placeholder={translations[language]?.Longitude || 'Longitude'} 
                      className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed" 
                      type="number"
                      step="any"
                      disabled={isFormLoading}
                    />
                  </div>
                </div>
                
                <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                  <button 
                    type="button"
                    onClick={closeFormModal}
                    className="px-6 py-3 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isFormLoading}
                  >
                    {translations[language]?.cancel}
                  </button>
                  <button 
                    type="submit" 
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    disabled={isFormLoading}
                  >
                    {isFormLoading && (
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    <span>
                      {isFormLoading 
                        ? (editIndex !== null ? 'Updating...' : 'Adding...') 
                        : (editIndex !== null ? 'Update Village' : (translations[language]?.AddVillage || 'Add Village'))
                      }
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Download Modal */}
        <DownloadModal
          isOpen={showDownloadModal}
          onClose={() => setShowDownloadModal(false)}
          title={translations[language]?.villagedata} 
          data={getDownloadData()}
          columns={downloadColumns}
          isLoading={isDownloadLoading}
          setIsLoading={setIsDownloadLoading}
          screenName="VillageManagement"
        />
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default VillageManagement;